import { useState } from 'react'
import { Link } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import { useDaftarTenant, useJalurMasuk, useKejadian } from '../../../domains/tenancy/api'
import { tanggalJam } from '../../../shared/lib/waktu'
import {
  Baris, Chip, Gagal, KepalaHalaman, Kosong, Memuat, Panel, Pilihan, Tabel, Tombol,
} from '../../../shared/ui'

/**
 * Payload mentah satu kejadian.
 *
 * Dimuat HANYA setelah ditekan, tidak pernah ikut render — setiap pembukaan menambah
 * satu baris di `audit_log` (ADR-052), dan baris audit yang lahir dari komponen yang
 * kebetulan ter-render membuat catatannya berhenti berarti.
 */
function Payload({ id, t }) {
  const q = useKejadian(id)
  if (q.isLoading) return <Memuat tinggi={3} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />
  return (
    <pre className="m-0 max-h-[420px] overflow-auto text-[11.5px] font-mono
                    bg-surface2 border border-line rounded-md p-3 whitespace-pre-wrap
                    break-words">
      {JSON.stringify(q.data?.payload ?? {}, null, 2)}
    </pre>
  )
}

const NADA_STATUS = {
  diproses: 'ok',
  diterima: 'warn',
  gagal: 'bad',
  tanpa_tenant: 'bad',
}

const URUTAN = ['gagal', 'tanpa_tenant', 'diterima', 'diproses']

export default function JalurMasuk() {
  const { t, locale } = useBahasa()
  const [tenant, setTenant] = useState('')
  const [status, setStatus] = useState('')
  // Satu payload terbuka pada satu waktu. Dua terbuka bersamaan berarti dua baris
  // audit untuk satu tindakan, dan tidak ada yang benar-benar dibaca dua-duanya.
  const [dibuka, setDibuka] = useState(null)

  const daftarTenant = useDaftarTenant()
  const q = useJalurMasuk({
    tenant: tenant || undefined,
    status: status || undefined,
    limit: 50,
  })

  const items = q.data?.items ?? []
  const nama = Object.fromEntries((daftarTenant.data ?? []).map((x) => [x.id, x.name]))

  return (
    <>
      <KepalaHalaman judul={t('jm.judul')} keterangan={t('jm.keterangan')} />

      {/* Ringkasan dibaca LEBIH DULU, dan urutannya bukan abjad: yang butuh tindakan
          di depan. Angkanya dihitung backend atas seluruh yang cocok penyaring —
          bukan atas halaman ini, karena "0 gagal" pada halaman yang kebetulan bersih
          adalah kesimpulan yang salah tentang sistem. */}
      {q.data && (
        <div className="flex flex-wrap gap-2">
          {URUTAN.map((s) => {
            const n = q.data.per_status[s] ?? 0
            return (
              <button
                key={s}
                onClick={() => setStatus(status === s ? '' : s)}
                aria-pressed={status === s}
                className={`flex items-baseline gap-2 bg-surface border rounded-md
                            px-3 py-2 cursor-pointer text-left
                            ${status === s ? 'border-accent' : 'border-line hover:border-lineStrong'}`}
              >
                <b className={`text-[17px] tabular ${n > 0 && (s === 'gagal' || s === 'tanpa_tenant') ? 'text-bad' : ''}`}>
                  {n}
                </b>
                <span className="text-[12px] text-ink2">{t(`jm.status.${s}`)}</span>
              </button>
            )
          })}
        </div>
      )}

      <Panel
        judul={t('jm.kejadian')}
        aksi={
          <span className="flex items-center gap-2">
            <Pilihan value={tenant} onChange={(e) => setTenant(e.target.value)}>
              <option value="">{t('jm.semuaTenant')}</option>
              {/* 'tanpa' BUKAN tenant, dan sengaja duduk di daftar yang sama:
                  pertanyaan "punya siapa ini" dan "tidak punya siapa-siapa" dijawab
                  dari satu tempat, bukan dua kontrol berbeda. */}
              <option value="tanpa">{t('jm.tanpaTenant')}</option>
              {(daftarTenant.data ?? []).map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </Pilihan>
            <Pilihan value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('jm.semuaStatus')}</option>
              {URUTAN.map((s) => (
                <option key={s} value={s}>{t(`jm.status.${s}`)}</option>
              ))}
            </Pilihan>
          </span>
        }
      >
        {q.isLoading && <Memuat tinggi={5} />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}
        {q.data && items.length === 0 && (
          <Kosong judul={t('jm.kosong')} keterangan={t('jm.kosongKeterangan')} />
        )}

        {items.length > 0 && (
          <Tabel
            kepala={[
              t('jm.kolWaktu'), t('jm.kolPlatform'), t('jm.kolAkun'),
              t('jm.kolTenant'), t('jm.kolStatus'), t('jm.kolSebab'), '',
            ]}
          >
            {items.flatMap((e) => [
              <Baris key={e.id}>
                <td className="font-mono text-[11.5px] whitespace-nowrap">
                  {tanggalJam(e.received_at, locale)}
                </td>
                <td className="text-ink2">{e.platform}</td>
                <td className="font-mono text-[11.5px]">{e.external_account_id ?? '—'}</td>
                <td>
                  {e.tenant_id ? (
                    <Link
                      to={`/tenant/${e.tenant_id}`}
                      className="text-ink2 hover:text-ink no-underline"
                    >
                      {nama[e.tenant_id] ?? e.tenant_id.slice(0, 8)}
                    </Link>
                  ) : (
                    <span className="text-bad font-semibold text-[12px]">
                      {t('jm.tanpaTenant')}
                    </span>
                  )}
                </td>
                <td>
                  <Chip nada={NADA_STATUS[e.status] ?? 'idle'}>
                    {t(`jm.status.${e.status}`)}
                  </Chip>
                </td>
                {/* Sebab kegagalan ditampilkan APA ADANYA dari backend. Ia satu-satunya
                    hal di baris ini yang menjelaskan, dan memotongnya jadi ringkasan
                    akan membuang bagian yang justru dicari. */}
                <td className="max-w-[380px]">
                  <span className="text-[12px] text-ink2 break-words">{e.error ?? '—'}</span>
                </td>
                <td className="text-right">
                  <Tombol
                    kecil
                    jenis="sekunder"
                    onClick={() => setDibuka(dibuka === e.id ? null : e.id)}
                  >
                    {dibuka === e.id ? t('umum.tutup') : t('jm.bukaPayload')}
                  </Tombol>
                </td>
              </Baris>,

              dibuka === e.id ? (
                <tr key={`${e.id}-payload`}>
                  <td colSpan={7} className="bg-surface2 px-3.5 py-3">
                    <div className="flex flex-col gap-2">
                      <p className="m-0 text-[11.5px] text-warn">{t('jm.pembukaanDicatat')}</p>
                      <Payload id={e.id} t={t} />
                    </div>
                  </td>
                </tr>
              ) : null,
            ])}
          </Tabel>
        )}
      </Panel>

      <p className="m-0 text-[11.5px] text-ink3">
        {t('jm.catatanIsi')}
      </p>
    </>
  )
}
